/**
 * Piece upload — « Déposer une pièce » from the « Pièces » tab of a contract
 * (fiche 22975) or of a claim (fiche 22976). Two calls through the ERP proxy
 * (contract §6 « Write endpoints »), orchestrated client-side:
 *
 * | step | endpoint slug                                   | method | `data`                                              |
 * |------|-------------------------------------------------|--------|-----------------------------------------------------|
 * | 1    | `document-create`                               | POST   | the document (`idTab`, `type` = `CD_DS`, base64, …) |
 * | 2    | `contrat-pmq-update` / `sinistre-pmq-update`    | PUT    | path `id_npol` / `id_sin` + `id_pmd`, body below    |
 *
 * - Call 1 → `{ id, num, message, isFileAllow: { status, content } }`. A
 *   `success: false` envelope, a non-blank `message`, `isFileAllow.status
 *   === false` or a missing `id` is a refusal: the flow stops (no call 2) and
 *   the error carries `isFileAllow.content` / `message` / the proxy message.
 * - Call 2 body: `{ dateReception, decision: 4, idArchivdoc: String(id) }`.
 *   Its failure (after call 1 archived the document) is a distinct stage
 *   (`piece`): there is no rollback API, and retrying archives the document
 *   a second time (accepted, documented).
 * - Dates (`creationDate`, `dateReception`) = today in Europe/Paris,
 *   `JJ/MM/AAAA`. `user` = the logged-in person (`erp.person.id`), never the
 *   session id. `SES` is appended by the proxy from `X-Erp-Session-Id`.
 */

import { todayDdMmYyyy } from '../utils/format';
import type { PieceUploadOrigin, PieceUploadTarget } from '../components/shared/pieceItems';
import {
  ERP_NO_REGISTRY_MESSAGE,
  erpEndpointPath,
  erpMessageOf,
  type ErpProxyResponse,
} from './erpProxy';
import { extranetApiClient } from './extranetApiClient';

/** Endpoint slugs of the upload (contract §6). */
export const PIECE_UPLOAD_ENDPOINTS = {
  documentCreate: 'document-create',
  contractPieceUpdate: 'contrat-pmq-update',
  claimPieceUpdate: 'sinistre-pmq-update',
} as const;

/** Call 1 constants per origin (fiches 22975 / 22976). */
const DOCUMENT_DESTINATION: Readonly<
  Record<PieceUploadOrigin, { service: string; tab: string }>
> = {
  contrat: { service: 'PROD', tab: 'POLICE' },
  sinistre: { service: 'SINISTRE', tab: 'SINISTRE' },
};

/** Call 2 `decision` constant (« pièce reçue »), sent as a number. */
export const PIECE_RECEIVED_DECISION = 4;

/** `contentType` of a file the browser gave no MIME type. */
export const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

/** The JSON body of call 1 (`document-create`), exactly the fiche's fields. */
export interface DocumentCreateBody {
  confid: '0';
  contentType: string;
  creationDate: string;
  datas: string;
  idTab: number;
  label: string;
  name: string;
  para: '';
  service: string;
  tab: string;
  type: string;
  color: '';
  user: number;
}

/** The document to archive: name, MIME type and base64 content (no `data:` prefix). */
export interface PieceUploadDocument {
  name: string;
  contentType: string;
  datas: string;
}

/** Call 1 answer (`data` of the proxy envelope). */
export interface DocumentCreateResult {
  id?: number | string | null;
  num?: string | null;
  message?: string | null;
  isFileAllow?: { status?: boolean | null; content?: string | null } | null;
}

/** Call 2 answer (`data` of the proxy envelope — claimflow's shape on the same resource). */
export interface PieceUpdateResult {
  id?: number | string | null;
  num?: string | null;
  message?: string | null;
}

/** Call 2: the endpoint slug and the proxy `data` (path params + body). */
export interface PieceUpdateRequest {
  endpoint: string;
  data: Record<string, string | number>;
}

/** Build the body of call 1 for a piece. */
export function buildDocumentCreateBody(
  target: PieceUploadTarget,
  document: PieceUploadDocument,
  user: number,
  today: string,
): DocumentCreateBody {
  const destination = DOCUMENT_DESTINATION[target.origin];
  return {
    confid: '0',
    contentType: document.contentType === '' ? DEFAULT_CONTENT_TYPE : document.contentType,
    creationDate: today,
    datas: document.datas,
    idTab: target.idTab,
    label: document.name,
    name: document.name,
    para: '',
    service: destination.service,
    tab: destination.tab,
    type: target.cdDs,
    color: '',
    user,
  };
}

/** Build call 2 (`contrat-pmq-update` / `sinistre-pmq-update`) for an archived document. */
export function buildPieceUpdateRequest(
  target: PieceUploadTarget,
  idArchivdoc: string,
  today: string,
): PieceUpdateRequest {
  const body = { dateReception: today, decision: PIECE_RECEIVED_DECISION, idArchivdoc };
  return target.origin === 'contrat'
    ? {
        endpoint: PIECE_UPLOAD_ENDPOINTS.contractPieceUpdate,
        data: { id_npol: target.parentId, id_pmd: target.idPmd, ...body },
      }
    : {
        endpoint: PIECE_UPLOAD_ENDPOINTS.claimPieceUpdate,
        data: { id_sin: target.parentId, id_pmd: target.idPmd, ...body },
      };
}

/**
 * Where the upload stopped: `read` (the file could not be read), `document`
 * (call 1 failed or was refused — nothing archived, no call 2), `piece`
 * (call 2 failed after the document was archived).
 */
export type PieceUploadStage = 'read' | 'document' | 'piece';

/** A failed upload; `detail` is the ERP / VEOS message when there is one. */
export class PieceUploadError extends Error {
  readonly stage: PieceUploadStage;
  readonly detail: string | null;

  constructor(stage: PieceUploadStage, detail: string | null) {
    super(detail ?? `Piece upload failed (${stage})`);
    this.name = 'PieceUploadError';
    this.stage = stage;
    this.detail = detail;
  }
}

/** Read a file as base64, without the `data:<mime>;base64,` prefix. */
export function readFileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma === -1 ? '' : result.slice(comma + 1));
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('FileReader failed'));
    };
    reader.readAsDataURL(file);
  });
}

/** A non-blank trimmed string, else `null`. */
function nonBlank(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** The archived document id of a call 1 answer, or a `document` error (refusal / no id). */
function archivedDocumentId(result: DocumentCreateResult | null | undefined): string {
  const message = nonBlank(result?.message);
  const allow = result?.isFileAllow;
  if (allow?.status === false || message !== null) {
    throw new PieceUploadError('document', nonBlank(allow?.content) ?? message);
  }
  const id = result?.id;
  const idText = typeof id === 'number' ? String(id) : nonBlank(id);
  if (idText === null) throw new PieceUploadError('document', null);
  return idText;
}

/** Input of {@link uploadPiece}. */
export interface UploadPieceInput {
  registrySlug: string | null | undefined;
  target: PieceUploadTarget;
  file: File;
  /** The logged-in person (`erp.person.id`) — `null` ⇒ the upload is refused. */
  userId: number | null;
  /** Clock of the « date du jour » (tests). */
  now?: Date;
}

/** A successful upload: the archived document id (call 2 `idArchivdoc`). */
export interface UploadPieceResult {
  idArchivdoc: string;
}

/**
 * Upload a piece: read the file, archive it (call 1), then mark the piece
 * received (call 2).
 *
 * @throws PieceUploadError — `stage` tells which step failed.
 */
export async function uploadPiece({
  registrySlug,
  target,
  file,
  userId,
  now,
}: UploadPieceInput): Promise<UploadPieceResult> {
  if (!registrySlug) throw new PieceUploadError('document', ERP_NO_REGISTRY_MESSAGE);
  if (userId === null) throw new PieceUploadError('document', null);

  let datas: string;
  try {
    datas = await readFileAsBase64(file);
  } catch {
    throw new PieceUploadError('read', null);
  }

  const today = todayDdMmYyyy(now);
  const body = buildDocumentCreateBody(
    target,
    { name: file.name, contentType: file.type, datas },
    userId,
    today,
  );

  // --- call 1: archive the document ---------------------------------------
  let created: ErpProxyResponse<DocumentCreateResult | null>;
  try {
    const response = await extranetApiClient.post<ErpProxyResponse<DocumentCreateResult | null>>(
      erpEndpointPath(registrySlug, PIECE_UPLOAD_ENDPOINTS.documentCreate),
      { data: body },
    );
    created = response.data;
  } catch (error: unknown) {
    throw new PieceUploadError('document', erpMessageOf(error));
  }
  if (!created.success) throw new PieceUploadError('document', erpMessageOf(created));
  const idArchivdoc = archivedDocumentId(created.data);

  // --- call 2: mark the piece received --------------------------------------
  const request = buildPieceUpdateRequest(target, idArchivdoc, today);
  let updated: ErpProxyResponse<PieceUpdateResult | null>;
  try {
    const response = await extranetApiClient.put<ErpProxyResponse<PieceUpdateResult | null>>(
      erpEndpointPath(registrySlug, request.endpoint),
      { data: request.data },
    );
    updated = response.data;
  } catch (error: unknown) {
    throw new PieceUploadError('piece', erpMessageOf(error));
  }
  if (!updated.success) throw new PieceUploadError('piece', erpMessageOf(updated));
  const refusal = nonBlank(updated.data?.message);
  if (refusal !== null) throw new PieceUploadError('piece', refusal);

  return { idArchivdoc };
}
